import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHero from '@/components/feature/PageHero';
import CtaSection from '@/components/feature/CtaSection';
import Reveal from '@/components/base/Reveal';
import Counter from '@/components/base/Counter';
import SectionHeading from '@/components/base/SectionHeading';
import CaseCard from '@/pages/realisations/components/CaseCard';
import CaseModal from '@/pages/realisations/components/CaseModal';
import { caseFigures, caseSectors, caseStudies } from '@/mocks/realisations';
import usePageMeta from '@/hooks/usePageMeta';

export default function Realisations() {
  usePageMeta(
    'Réalisations industrielles à Douala | SGSCTIC',
    'Cuves, réseaux vapeur, charpentes, silos et arrêts techniques : découvrez les réalisations industrielles de SGSCTIC au Cameroun, filtrées par secteur et détaillées en études de cas.',
  );
  const [activeSector, setActiveSector] = useState(caseSectors[0]);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (activeSector === caseSectors[0]) return caseStudies;
    return caseStudies.filter((study) => study.sector === activeSector);
  }, [activeSector]);

  const openStudy = caseStudies.find((study) => study.slug === openSlug) ?? null;

  const availableSectors = caseSectors.filter(
    (sector) => sector === caseSectors[0] || caseStudies.some((study) => study.sector === sector),
  );

  return (
    <SiteLayout>
      <PageHero
        breadcrumb="Réalisations"
        label="Chantiers & études de cas"
        title="Des ouvrages qui tiennent dans le temps"
        description="Cuves, réseaux vapeur, charpentes, silos, arrêts techniques : découvrez comment nous résolvons les contraintes réelles de vos sites industriels."
        image="https://sgsctic.com/wp-content/uploads/2026/01/WhatsApp-Image-2025-12-19-at-10.05.26.jpeg"
        imageAlt="Assemblage d’un ouvrage chaudronné dans l’atelier SGSCTIC"
      />

      {/* Key figures */}
      <section className="border-b border-background-200 bg-background-100 py-10 md:py-12">
        <div className="container-wide">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-background-200 md:grid-cols-4">
            {caseFigures.map((figure) => (
              <div key={figure.label} className="bg-background-50 px-5 py-6">
                <p className="font-heading text-4xl font-semibold leading-none text-foreground-950">
                  <Counter value={figure.value} suffix={figure.suffix} />
                </p>
                <p className="mt-2 text-[10px] uppercase leading-snug tracking-[0.14em] text-foreground-600">
                  {figure.label}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-foreground-400">{figure.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="bg-background-50 py-16 md:py-24">
        <div className="container-wide">
          <Reveal>
            <SectionHeading
              label="Portfolio industriel"
              title="Filtrer par secteur d’activité"
              description="Sélectionnez un secteur pour afficher les chantiers correspondants, puis ouvrez une étude de cas pour voir le défi, notre réponse et les résultats."
            />
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-9 flex flex-wrap items-center gap-2">
              {availableSectors.map((sector) => {
                const isActive = sector === activeSector;
                const count =
                  sector === caseSectors[0]
                    ? caseStudies.length
                    : caseStudies.filter((study) => study.sector === sector).length;
                return (
                  <button
                    key={sector}
                    type="button"
                    onClick={() => setActiveSector(sector)}
                    aria-pressed={isActive}
                    className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2.5 text-[11px] uppercase tracking-[0.1em] transition-colors duration-300 ${
                      isActive
                        ? 'border-primary-500 bg-primary-500 text-background-50'
                        : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-foreground-950'
                    }`}
                  >
                    {sector}
                    <span
                      className={`font-label text-[10px] ${
                        isActive ? 'text-background-100' : 'text-foreground-400'
                      }`}
                    >
                      {count.toString().padStart(2, '0')}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          <p className="mt-5 font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
            {filtered.length.toString().padStart(2, '0')} chantier
            {filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''} ·{' '}
            {activeSector}
          </p>

          <div key={activeSector} className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((study, index) => (
              <Reveal key={study.slug} delay={index * 70}>
                <CaseCard study={study} onOpen={() => setOpenSlug(study.slug)} />
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-7 rounded-lg border border-background-200 bg-background-100 p-10 text-center">
              <p className="text-sm text-foreground-600">
                Aucun chantier publié pour ce secteur pour le moment.
              </p>
            </div>
          ) : null}
        </div>
      </section>

      {/* Method reminder */}
      <section className="relative overflow-hidden bg-foreground-950 py-16 md:py-20">
        <div className="absolute inset-0 hatch-lines-light opacity-25" />
        <div className="container-wide relative z-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-16">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-primary-500" />
                <span className="label-mono text-primary-300">Derrière chaque chantier</span>
              </div>
              <h2 className="display-title mt-5 text-4xl text-background-50 md:text-5xl">
                La même exigence,
                <br />
                quel que soit le volume
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-background-300">
                Relevé sur site, étude, préfabrication en atelier, montage et contrôle : la méthode
                ne change pas. Seule l’échelle varie, d’une réparation ponctuelle à un ouvrage de
                plusieurs dizaines de tonnes.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/offres"
                  className="btn-shine flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-background-50 transition-colors hover:bg-primary-600"
                >
                  Voir nos expertises
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-background-50 transition-colors hover:bg-background-50 hover:text-foreground-950"
                >
                  Parler de mon projet
                </Link>
              </div>
            </Reveal>

            <Reveal variant="fade" delay={120}>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-ruler-line', label: 'Relevé & étude' },
                  { icon: 'ri-hammer-line', label: 'Préfabrication' },
                  { icon: 'ri-truck-line', label: 'Livraison & levage' },
                  { icon: 'ri-shield-check-line', label: 'Contrôle & réception' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-md border border-background-50/15 bg-background-50/5 px-5 py-6"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
                      <i className={`${item.icon} text-lg`} />
                    </span>
                    <p className="mt-4 text-sm font-semibold uppercase tracking-wide text-background-100">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaSection
        title="Votre chantier sera"
        accent="le prochain ?"
        description="Décrivez-nous votre ouvrage, votre contrainte de site ou votre arrêt de production : nous revenons avec une lecture technique et un chiffrage."
      />

      {openStudy ? <CaseModal study={openStudy} onClose={() => setOpenSlug(null)} /> : null}
    </SiteLayout>
  );
}